import React, { useEffect, useCallback } from 'react';
import { Drawer, Form, Button, Space, Spin, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import VipSuppIdForm from './VipSuppIdForm';
import { saveVipSuppId } from '../api';
import { VipSuppIdFormData, VipSuppIdRecord } from '../types';
import { QUERY_KEYS, DRAWER_CONFIG, FORM_DEFAULT_VALUES } from '../constants';

interface VipSuppIdDrawerProps {
  open: boolean;
  editingVipSuppId?: VipSuppIdRecord | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const VipSuppIdDrawer: React.FC<VipSuppIdDrawerProps> = ({
  open,
  editingVipSuppId,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<VipSuppIdFormData>();
  const isEdit = !!editingVipSuppId?.id;

  // 保存（新增/编辑统一）
  const saveMutation = useMutation({
    mutationFn: saveVipSuppId,
    onSuccess: (_, variables) => {
      message.success(variables.id ? '更新成功' : '创建成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VIP_SUPPID_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VIP_SUPPID_STATS] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error, variables) => {
      console.error('保存失败:', error);
      message.error(error.message || (variables.id ? '更新失败' : '创建失败'));
    },
  });

  // 加载数据到表单（从列表数据中获取）
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (editingVipSuppId && isEdit) {
      const formValues: VipSuppIdFormData = {
        id: editingVipSuppId.id,
        beautifulNumber: editingVipSuppId.beautifulNumber !== undefined && editingVipSuppId.beautifulNumber !== null
          ? String(editingVipSuppId.beautifulNumber)
          : editingVipSuppId.suppId || '',
      };

      form.setFieldsValue(formValues);
    } else {
      form.resetFields();
      form.setFieldsValue(FORM_DEFAULT_VALUES);
    }
  }, [editingVipSuppId, isEdit, open, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      // 如果是编辑，添加 id 字段
      const submitData: VipSuppIdFormData = {
        ...values,
        ...(isEdit && editingVipSuppId?.id ? { id: editingVipSuppId.id } : {}),
      };
      
      saveMutation.mutate(submitData);
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请检查表单填写是否正确');
    }
  }, [form, isEdit, editingVipSuppId, saveMutation]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const loading = saveMutation.isPending;

  return (
    <Drawer
      title={isEdit ? '编辑 VIP SUPPID' : '新增 VIP SUPPID'}
      width={DRAWER_CONFIG.WIDTH}
      onClose={handleClose}
      open={open}
      placement={DRAWER_CONFIG.PLACEMENT}
      maskClosable={false}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <VipSuppIdForm form={form} isEdit={isEdit} />
      </Spin>
    </Drawer>
  );
};

export default VipSuppIdDrawer;

